import { auth2FAAPI, AuthError } from '../api/authAPI2FA';
import StorageService from '../storage/storageService';
import { profileAPI } from '../api/profileAPI';
import { resetApiBaseUrl, setApiBaseUrlForCompany } from '../../utils/storage.js';
import { authStorage } from '../../tenancy/authStorage.js';
import { languageStore } from '../../i18n/languageStore.js';

class Auth2FAService {
  constructor() {
    this.tempToken = authStorage.getTwoFactorToken();
    this.pendingLoginData = null;
  }

  // ============ LOGIN WITH 2FA SUPPORT ============
  async login(phoneNumber, password, role = null) {
    try {
      resetApiBaseUrl();
      const result = await auth2FAAPI.login(phoneNumber, password, role);

      if (result.requires2FA) {
        // Store temp token for 2FA verification
        this.tempToken = result.tempToken;
        authStorage.setTwoFactorToken(result.tempToken);
        this.pendingLoginData = { phoneNumber, role };

        return {
          success: false,
          requires2FA: true,
          message: result.message
        };
      }

      // Regular login without 2FA - save token and expiry
      await this.saveAuthData(result.token, result.expiry, result.user);

      return {
        success: true,
        requires2FA: false,
        user: result.user,
        token: result.token
      };
    } catch (error) {
      throw error;
    }
  }

  // ============ VERIFY 2FA CODE ============
  async verify2FA(code) {
    if (!this.tempToken) {
      throw new AuthError(
        'Nu există o sesiune de verificare activă. Te rugăm să te autentifici din nou.',
        400,
        'NO_TEMP_TOKEN'
      );
    }

    try {
      resetApiBaseUrl();
      const result = await auth2FAAPI.verify2FA(this.tempToken, code);

      // Save auth data
      await this.saveAuthData(result.token, result.expiry, result.user);

      // Clear temp data
      this.clearTempData();

      return {
        success: true,
        user: result.user,
        token: result.token
      };
    } catch (error) {
      // If code is invalid but we have attempts remaining, keep temp token
      if (error.errorCode === 'INVALID_CODE' && error.attemptsRemaining > 0) {
        throw error;
      }

      // For other errors or when no attempts remain, clear temp data
      if (error.errorCode === 'TOO_MANY_ATTEMPTS' ||
          error.errorCode === 'CODE_EXPIRED' ||
          error.errorCode === 'SESSION_EXPIRED') {
        this.clearTempData();
      }

      throw error;
    }
  }

  // ============ RESEND 2FA CODE ============
  async resend2FACode() {
    if (!this.tempToken) {
      throw new AuthError(
        'Nu există o sesiune de verificare activă. Te rugăm să te autentifici din nou.',
        400,
        'NO_TEMP_TOKEN'
      );
    }

    try {
      resetApiBaseUrl();
      const result = await auth2FAAPI.resend2FACode(this.tempToken);

      // IMPORTANT: Update temp token with the new one!
      this.tempToken = result.tempToken;
      authStorage.setTwoFactorToken(result.tempToken);

      return {
        success: true,
        message: result.message
      };
    } catch (error) {
      // If session expired, clear temp data
      if (error.errorCode === 'SESSION_EXPIRED') {
        this.clearTempData();
      }
      throw error;
    }
  }

  // ============ LOGOUT ============
  async logout() {
    const token = StorageService.getAuthToken();

    if (token) {
      // Try to logout from server (don't wait for response)
      auth2FAAPI.logout(token).catch(console.error);
    }

    // Clear local storage
    await this.clearAuthData();
    this.clearTempData();

    return { success: true };
  }

  // ============ LOGOUT ALL DEVICES ============
  async logoutAllDevices() {
    const token = StorageService.getAuthToken();

    if (!token) {
      throw new AuthError(
        'Nu ești autentificat.',
        401,
        'NOT_AUTHENTICATED'
      );
    }

    await auth2FAAPI.logoutAllDevices(token);
    await this.clearAuthData();
    this.clearTempData();

    return { success: true };
  }

  // ============ SESSION MANAGEMENT ============
  async getActiveSessions() {
    const token = StorageService.getAuthToken();

    if (!token) {
      throw new AuthError(
        'Nu ești autentificat.',
        401,
        'NOT_AUTHENTICATED'
      );
    }

    return await auth2FAAPI.getActiveSessions(token);
  }

  async revokeSession(sessionId) {
    const token = StorageService.getAuthToken();

    if (!token) {
      throw new AuthError(
        'Nu ești autentificat.',
        401,
        'NOT_AUTHENTICATED'
      );
    }

    return await auth2FAAPI.revokeSession(token, sessionId);
  }

  // ============ 2FA MANAGEMENT ============
  async get2FAStatus() {
    const token = StorageService.getAuthToken();

    if (!token) {
      throw new AuthError(
        'Nu ești autentificat.',
        401,
        'NOT_AUTHENTICATED'
      );
    }

    return await auth2FAAPI.get2FAStatus(token);
  }

  async enable2FA() {
    const token = StorageService.getAuthToken();

    if (!token) {
      throw new AuthError(
        'Nu ești autentificat.',
        401,
        'NOT_AUTHENTICATED'
      );
    }

    return await auth2FAAPI.enable2FA(token);
  }

  async disable2FA(password) {
    const token = StorageService.getAuthToken();

    if (!token) {
      throw new AuthError(
        'Nu ești autentificat.',
        401,
        'NOT_AUTHENTICATED'
      );
    }

    return await auth2FAAPI.disable2FA(token, password);
  }

  // ============ TOKEN MANAGEMENT ============
  async saveAuthData(token, expiry, user) {
    StorageService.setAuthToken(token);
    StorageService.set('tokenExpiry', expiry);

    let userData = user || null;
    if (userData) {
      StorageService.set('userData', userData);
    }

    try {
      const profile = await profileAPI.getProfile(token);
      userData = { ...(userData || {}), ...profile };
      StorageService.set('userData', userData);
    } catch (error) {
      // Keep login usable even if profile is not available immediately.
    }

    setApiBaseUrlForCompany(userData || {});
    languageStore.resetUserLanguageSelection();
    userData = languageStore.applyUser(userData);
    if (userData) StorageService.set('userData', userData);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('atrux:user-language', { detail: { user: userData } }));
    }
  }

  async clearAuthData() {
    StorageService.clearAuthData();
  }

  clearTempData() {
    this.tempToken = null;
    this.pendingLoginData = null;
    authStorage.clearTwoFactorToken();
  }

  // ============ TOKEN VALIDATION ============
  isAuthenticated() {
    const token = StorageService.getAuthToken();
    const expiry = StorageService.get('tokenExpiry');

    if (!token) return false;
    if (!expiry) return true; // No expiry stored, assume valid

    // Check if token is expired
    const expiryDate = new Date(expiry);
    const now = new Date();

    return expiryDate > now;
  }

  async checkAndRefreshToken() {
    if (!this.isAuthenticated()) {
      await this.clearAuthData();
      return false;
    }
    return true;
  }

  getToken() {
    if (!this.isAuthenticated()) {
      return null;
    }
    return StorageService.getAuthToken();
  }

  getUser() {
    return StorageService.get('userData');
  }

  getTokenExpiry() {
    return StorageService.get('tokenExpiry');
  }

  // ============ UTILITY METHODS ============
  hasPending2FA() {
    return !!this.tempToken;
  }

  getPendingLoginData() {
    return this.pendingLoginData;
  }

  // Method to restore 2FA state (useful for page refreshes)
  restore2FAState(tempToken, phoneNumber) {
    this.tempToken = tempToken;
    authStorage.setTwoFactorToken(tempToken);
    this.pendingLoginData = { phoneNumber };
  }
}

export default new Auth2FAService();
export { AuthError };
